import {
  AfterViewInit,
  Component,
  HostListener,
  Input,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  ChangeDetectionStrategy
} from '@angular/core';

import { each } from 'lodash';

import { Enums } from '../../../../../../../configs/Enums';
import { TICKETS_PATHS } from '../../../../../../tickets/tickets';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-tickets-matrix-new',
  templateUrl: './tickets-matrix.component.html',
  styleUrls: ['./tickets-matrix.component.scss'],
  standalone: false,
})
export class TicketsMatrixComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  public clamp = require('text-overflow-clamp');

  public Enums = Enums;
  public moment = require('moment');
  public TICKETS_PATHS = TICKETS_PATHS;
  public dateShortFormat = Enums.dates.shortFormat;
  public currentDate = this.moment().startOf('day').format(this.dateShortFormat);

  @Input() ticketsCurrentFilter;
  @Input() ticketsPeriodFilter;
  @Input() ticketsMultipleAssign;
  @Input() tickets;
  @Input() filter;
  @Input() brigadeAssign;

  // Глобальная верхняя шкала удалена, ссылки не нужны
  @ViewChild('timelineContainer') timelineContainer: ElementRef<HTMLElement>;
  @ViewChild('cardsContainer') cardsContainer: ElementRef<HTMLElement>;

  // Временная шкала
  public timelineHours: string[] = [];
  public hourWidth = 143; // px (~30% меньше 204)
  public totalTimelineWidth = 0;

  // Карточки с позиционированием
  public positionedTickets: any[] = [];

  // Текущее время для отображения линии в контейнере
  public currentTime: string = null;
  public currentTimePosition: number = -1;
  private isAnimating = false;
  private isSyncing = false;
  private scrollThrottleTimeout: any;

  @HostListener('window:scroll.out-zone', []) // notice out-zone
  onWindowScroll() {
    const intervals = document.querySelectorAll('.intervals');

    each(intervals, (interval: HTMLElement) => {
      const intervalsWrapper: HTMLElement = interval.parentElement;
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop;
      const topCoords = intervalsWrapper.getBoundingClientRect().top;

      if (scrollTop >= topCoords + scrollTop - 112) {
        interval.style.width = intervalsWrapper.offsetWidth + 'px';
        interval.classList.add('intervals--fixed');
      } else {
        interval.style.width = 'auto';
        interval.classList.remove('intervals--fixed');
      }
    });
  }

  constructor(private cdr: ChangeDetectorRef) {}

  ngOnInit() {
    this.generateTimeline();
    this.processTicketsForTimeline();
    this.setCurrentTime();

    // Обновляем текущее время каждую минуту
    setInterval(() => {
      this.setCurrentTime();
    }, 60000);
  }

  ngOnChanges(changes: SimpleChanges) {
    // Если изменились данные tickets или фильтры, пересчитываем позиционирование
    if (changes['tickets'] || changes['ticketsCurrentFilter']) {
      console.log('Matrix component: ngOnChanges triggered', changes);
      this.processTicketsForTimeline();
      // Принудительно обновляем представление
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    this.setEllipsis();
    // Синхронизация скролла после инициализации
    setTimeout(() => {
      this.setupScrollSync();
    }, 100);
  }

  // Генерация временной шкалы с 9:00 до 22:00
  generateTimeline() {
    this.timelineHours = [];
    for (let hour = 9; hour < 22; hour++) {
      // Изменено <= на <, чтобы исключить 22:00-23:00
      this.timelineHours.push(`${hour}:00 - ${hour + 1}:00`);
    }
    this.totalTimelineWidth = this.timelineHours.length * this.hourWidth;
  }

  // Обработка заявок для размещения на временной шкале
  processTicketsForTimeline() {
    this.positionedTickets = [];

    if (!this.tickets.dataMatrix) return;

    this.tickets.dataMatrix.forEach((dateData) => {
      if (!dateData.intervals.isShow || !dateData.intervals.intervals) return;

      const dateTickets = [];

      // Собираем все заявки за день
      const allTickets = [];
      dateData.intervals.intervals.forEach((interval) => {
        if (interval.tickets && interval.tickets.length > 0) {
          interval.tickets.forEach((ticket) => {
            if (ticket.isShow) {
              allTickets.push(ticket);
            }
          });
        }
      });

      // Сортируем заявки по времени начала для лучшего размещения
      allTickets.sort((a, b) => {
        const timeA = this.moment(a.hd_schedule_intbegin);
        const timeB = this.moment(b.hd_schedule_intbegin);
        return timeA.diff(timeB);
      });

      // Обрабатываем отсортированные заявки
      allTickets.forEach((ticket) => {
        const position = this.calculateTicketPosition(ticket);
        const width = this.calculateTicketWidth(ticket);
        const row = this.getTicketRow(ticket, dateTickets);

        // Добавляем свойства прямо к оригинальному объекту заявки
        // чтобы сохранить связь с dataFlatList для массовых действий
        ticket.position = position;
        ticket.width = width;
        ticket.row = row;
        ticket.isStretched = width > this.hourWidth;
        ticket.isCompact = width < this.hourWidth; // Карточка меньше часа - компактный вид

        dateTickets.push(ticket);
      });

      if (dateTickets.length > 0) {
        this.positionedTickets.push({
          date: dateData.date,
          tickets: dateTickets,
          containerHeight: this.calculateContainerHeight(dateTickets),
        });
      }
    });
  }

  // Расчет позиции карточки на временной шкале
  calculateTicketPosition(ticket: any): number {
    const startTime = this.moment(ticket.hd_schedule_intbegin);
    const startHour = startTime.hour();
    const startMinute = startTime.minute();

    // Если заявка начинается раньше 9:00, позиционируем в начале
    if (startHour < 9) {
      return 0;
    }

    // Если заявка начинается позже или в 22:00, позиционируем в конце (последний слот 21:00-22:00)
    if (startHour >= 22) {
      return (21 - 9) * this.hourWidth; // 21:00-22:00 это последний слот
    }

    // Рассчитываем точную позицию
    const hourOffset = startHour - 9;
    const minuteOffset = (startMinute / 60) * this.hourWidth;

    return hourOffset * this.hourWidth + minuteOffset;
  }

  // Расчет ширины карточки
  calculateTicketWidth(ticket: any): number {
    const startTime = this.moment(ticket.hd_schedule_intbegin);
    const endTime = this.moment(ticket.hd_schedule_intend);
    const durationMinutes = endTime.diff(startTime, 'minutes');

    // Минимальная ширина - 30 минут (половина часа), учитываем margin
    const marginOffset = 4; // 2px слева + 2px справа
    const minWidth = this.hourWidth / 2 - marginOffset; // 30 минут
    const calculatedWidth = (durationMinutes / 60) * this.hourWidth - marginOffset;

    const width = Math.max(minWidth, calculatedWidth);

    // Устанавливаем флаги для компактности
    ticket.isCompact = width < this.hourWidth - marginOffset; // Меньше часа
    ticket.isExtraCompact = width < 80; // Очень узкие карточки (примерно 30-40 мин)
    ticket.isStretched = durationMinutes > 60; // Больше часа

    return width;
  }

  // Определение ряда для карточки (избежание перекрытий)
  getTicketRow(ticket: any, existingTickets: any[]): number {
    const ticketStart = this.calculateTicketPosition(ticket);
    const ticketEnd = ticketStart + this.calculateTicketWidth(ticket);
    const bufferTime = 4; // Минимальный буфер между карточками

    // Находим максимальный ряд среди существующих карточек
    let maxRow = -1;
    for (const existingTicket of existingTickets) {
      if (existingTicket.row > maxRow) {
        maxRow = existingTicket.row;
      }
    }

    // Проверяем каждый ряд от 0 до maxRow+1
    for (let row = 0; row <= maxRow + 1; row++) {
      if (this.canFitInRow(ticketStart, ticketEnd, row, existingTickets, bufferTime)) {
        return row;
      }
    }

    // Если не нашли место, создаем новый ряд
    return maxRow + 1;
  }

  // Проверяем, может ли карточка поместиться в указанном ряду
  private canFitInRow(ticketStart: number, ticketEnd: number, row: number, existingTickets: any[], bufferTime: number): boolean {
    const ticketsInRow = existingTickets.filter((t) => t.row === row);

    // Если в ряду нет карточек, то место есть
    if (ticketsInRow.length === 0) {
      return true;
    }

    // Сортируем карточки в ряду по позиции
    ticketsInRow.sort((a, b) => a.position - b.position);

    // Проверяем все возможные позиции для размещения карточки
    for (let i = 0; i <= ticketsInRow.length; i++) {
      let canPlace = true;

      // Проверяем пересечение с предыдущей карточкой (если есть)
      if (i > 0) {
        const prevTicket = ticketsInRow[i - 1];
        const prevEnd = prevTicket.position + prevTicket.width;
        if (ticketStart < prevEnd + bufferTime) {
          canPlace = false;
        }
      }

      // Проверяем пересечение со следующей карточкой (если есть)
      if (i < ticketsInRow.length && canPlace) {
        const nextTicket = ticketsInRow[i];
        if (ticketEnd + bufferTime > nextTicket.position) {
          canPlace = false;
        }
      }

      // Если можем разместить в этой позиции, возвращаем true
      if (canPlace) {
        return true;
      }
    }

    return false;
  }

  // Расчет высоты контейнера на основе количества рядов
  calculateContainerHeight(tickets: any[]): number {
    if (tickets.length === 0) return 420; // Минимальная высота для пустого контейнера

    const maxRow = tickets.reduce((max, ticket) => Math.max(max, ticket.row), 0);
    const rowHeight = 420; // Высота ряда (соответствует значению в template)
    const minHeight = 420; // Минимальная высота контейнера
    const bottomPadding = 50; // Дополнительное место снизу для чекбоксов

    return Math.max(minHeight, (maxRow + 1) * rowHeight + bottomPadding);
  }

  // Синхронизация горизонтального скролла
  setupScrollSync() {
    // Раньше была синхронизация глобальной шкалы и общего контейнера.
    // Теперь используются только локальные таймлайны — здесь ничего не требуется.
  }

  // Прокрутка влево
  scrollLeft() {
    if (this.timelineContainer) {
      const container = this.timelineContainer.nativeElement;
      container.scrollBy({ left: -this.hourWidth * 2, behavior: 'smooth' });
    }
  }

  // Прокрутка вправо
  scrollRight() {
    if (this.timelineContainer) {
      const container = this.timelineContainer.nativeElement;
      container.scrollBy({ left: this.hourWidth * 2, behavior: 'smooth' });
    }
  }

  // Локальная прокрутка по стрелкам для таймлайна конкретной даты
  scrollLocalLeft(dateTimeline: HTMLElement, dateCards?: HTMLElement) {
    if (!dateTimeline) return;
    const target = this.getAlignedTargetLeft(dateTimeline, -1, 2);
    this.animateScrollPair(dateTimeline, dateCards, target, 350);
  }

  scrollLocalRight(dateTimeline: HTMLElement, dateCards?: HTMLElement) {
    if (!dateTimeline) return;
    const target = this.getAlignedTargetLeft(dateTimeline, 1, 2);
    this.animateScrollPair(dateTimeline, dateCards, target, 350);
  }

  private getAlignedTargetLeft(el: HTMLElement, direction: 1 | -1, stepHours: number): number {
    const stepPx = this.hourWidth * stepHours;
    const current = el.scrollLeft;
    const maxLeft = Math.max(0, this.totalTimelineWidth - el.clientWidth);

    const k = current / stepPx;
    const baseK = Math.round(k);
    const nextK = baseK + direction;
    const target = Math.min(maxLeft, Math.max(0, nextK * stepPx));
    return target;
  }

  private animateScroll(el: HTMLElement, to: number, duration = 400) {
    const from = el.scrollLeft;
    const change = to - from;
    if (change === 0) return;
    const start = performance.now();
    this.isAnimating = true;

    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const tick = (now: number) => {
      const elapsed = Math.min(1, (now - start) / duration);
      const eased = easeInOut(elapsed);
      el.scrollLeft = from + change * eased;
      if (elapsed < 1) {
        requestAnimationFrame(tick);
      } else {
        this.isAnimating = false;
      }
    };

    requestAnimationFrame(tick);
  }

  // Анимация для пары: таймлайн + контейнер карточек
  private animateScrollPair(timelineEl: HTMLElement, cardsEl: HTMLElement | undefined, to: number, duration = 400) {
    const from = timelineEl.scrollLeft;
    const change = to - from;
    if (change === 0) return;

    // Останавливаем предыдущую анимацию
    if (this.isAnimating) {
      this.isAnimating = false;
    }

    const start = performance.now();
    this.isAnimating = true;

    const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);
    const tick = (now: number) => {
      if (!this.isAnimating) return; // Проверяем, не была ли анимация остановлена

      const elapsed = Math.min(1, (now - start) / duration);
      const eased = easeInOut(elapsed);
      const val = from + change * eased;

      timelineEl.scrollLeft = val;
      if (cardsEl && cardsEl.scrollLeft !== val) {
        cardsEl.scrollLeft = val;
      }

      if (elapsed < 1) {
        requestAnimationFrame(tick);
      } else {
        this.isAnimating = false;
      }
    };

    requestAnimationFrame(tick);
  }

  // Обработчик синхронизированного скролла
  onTimelineScroll(event: Event) {
    // Глобальная шкала удалена; обработчик оставлен пустым на случай привязок в шаблоне
  }

  // Универсальная синхронизация прокрутки по горизонтали для пары элементов (локальные таймлайны)
  syncHorizontalScroll(src: HTMLElement, dst: HTMLElement) {
    if (this.isAnimating) return;
    if (!src || !dst) return;

    // Throttling для предотвращения частых вызовов
    if (this.scrollThrottleTimeout) {
      clearTimeout(this.scrollThrottleTimeout);
    }

    this.scrollThrottleTimeout = setTimeout(() => {
      const left = src.scrollLeft;
      if (dst.scrollLeft !== left) {
        dst.scrollLeft = left;
      }
    }, 16); // ~60fps
  }

  // Нижний общий скроллбар удалён

  changeOpenedIntervals(intervals): void {
    intervals.isOpen = !intervals.isOpen;

    if (intervals.isOpen) {
      setTimeout(() => {
        this.setEllipsis();
      });
    }
  }

  setEllipsis(): void {
    const self = this;
    const clamps = document.querySelectorAll('.card-content__address');

    if (clamps && clamps.length) {
      each(clamps, (clampElement) => self.clamp(clampElement, 2));
    }
  }

  setCurrentTime(): void {
    this.currentTime = this.moment().format('HH:mm');
    this.currentTimePosition = this.calculateCurrentTimePosition();
  }

  // Расчет позиции текущего времени на временной шкале
  calculateCurrentTimePosition(): number {
    if (!this.currentTime) return -1;

    const now = this.moment();
    const currentHour = now.hour();
    const currentMinute = now.minute();

    // Если текущее время вне диапазона 9:00-22:00, не показываем метку
    if (currentHour < 9 || currentHour >= 22) {
      return -1;
    }

    // Рассчитываем позицию относительно начала шкалы (9:00)
    const hourOffset = currentHour - 9;
    const minuteOffset = (currentMinute / 60) * this.hourWidth;

    return hourOffset * this.hourWidth + minuteOffset;
  }

  // Получение текущей позиции с учетом скролла
  getCurrentTimePosition(): number {
    return this.currentTimePosition;
  }

  // Проверяем, нужно ли показывать метку времени (только для сегодняшней даты)
  shouldShowCurrentTime(): boolean {
    const today = this.moment().format(this.dateShortFormat);
    return this.positionedTickets.some((dateData) => dateData.date === today) && this.currentTimePosition >= 0;
  }

  ngOnDestroy(): void {
    // Очищаем таймауты при уничтожении компонента
    if (this.scrollThrottleTimeout) {
      clearTimeout(this.scrollThrottleTimeout);
    }
  }
}
